import { CODE_ENV_LIST, JOIN_TYPE } from '@/common/constants/ConstCode';
import { normalizeSocialPhone } from '@/common/utils/module/FormattingSupport';

const { NAVER_CLIENT_ID, GOOGLE_CLIENT_ID } = CODE_ENV_LIST;

/**
 * 260807: 카카오 kakao_account 에서 생년월일(YYYY-MM-DD)을 조립한다.
 * 카카오는 출생연도(birthyear='YYYY')와 생일(birthday='MMDD')을 별개 동의항목으로 주기 때문에
 * 둘 다 있어야 날짜가 완성된다. 하나만 동의한 경우 반쪽 값을 넣으면 형식 검증에 걸려 오히려
 * 가입을 방해하므로 빈 문자열을 돌려주고 사용자가 직접 입력하게 둔다.
 * 음력(birthday_type='LUNAR')도 제외 — 저장 스키마가 양력 기준이라 그대로 넣으면 틀린 값이 된다.
 */
const buildKakaoBirthDate = account => {
  const year = account?.birthyear || '';
  const mmdd = account?.birthday || '';
  if (!/^\d{4}$/.test(year) || !/^\d{4}$/.test(mmdd)) return '';
  if (account?.birthday_type && account.birthday_type !== 'SOLAR') return '';
  return `${year}-${mmdd.slice(0, 2)}-${mmdd.slice(2, 4)}`;
};

/**
 * 카카오 웹 로그인
 * - Kakao JS SDK 사용 (index.html에서 로드, main.js에서 초기화 완료)
 * - 팝업 방식으로 로그인 후 사용자 정보 반환
 * @returns {Promise<{id: string, name: string, email: string, phone: string, accessToken: string}>}
 */
export const webKakaoLogin = () => {
  return new Promise((resolve, reject) => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      reject(new Error('Kakao SDK가 초기화되지 않았습니다.'));
      return;
    }

    window.Kakao.Auth.login({
      success: () => {
        // 260720 P0-B: BE /api/user/log/in 서버검증(KakaoTokenVerifier)에 실어 보낼 카카오 access_token.
        // 로그인 성공 직후 SDK가 보관 중인 토큰을 그대로 읽는다(재요청 불필요).
        const accessToken = window.Kakao.Auth.getAccessToken();
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: res => {
            const account = res.kakao_account || {};
            // 260807 '이름' 동의항목 승인 후: 실명(account.name)이 오면 그걸 쓴다.
            //   미동의면 기존대로 닉네임을 프리필한다(빈칸으로 두면 오히려 후퇴).
            //   단 닉네임은 실명이 아니므로 이름 스텝을 건너뛰면 안 된다 → isRealName 으로 구분한다.
            const realName = account.name || '';
            resolve({
              id: String(res.id),
              name: realName || account.profile?.nickname || '',
              // 이름 스텝 스킵 자격. 실명일 때만 true — SignUp.canSkipNameStep 이 이 값을 본다.
              isRealName: !!realName,
              email: account.email || '',
              // 260807 '생일'+'출생연도' 동의항목 승인 후: 둘을 합쳐 생년월일(YYYY-MM-DD)을 만든다.
              //   ⚠️ 카카오 birthday 는 하이픈 없는 'MMDD' 다(네이버 mobile 쪽 'MM-DD' 와 다름).
              //   ⚠️ birthday_type 이 LUNAR(음력)면 프리필하지 않는다 — tblUser.birthDate 는 양력
              //      기준이라 음력 값을 그대로 넣으면 틀린 생년월일이 저장된다.
              //   둘 중 하나만 동의하면 날짜가 완성되지 않으므로 빈값 → 사용자가 직접 입력.
              birthDate: buildKakaoBirthDate(account),
              // 260807: 웹 경로가 phone을 ''로 하드코딩해 카카오 전화번호가 가입폼에 전혀 안 넘어왔다
              //   (앱은 MainActivity.tryKakaoLogin이 kakaoAccount.phoneNumber를 넘기는데 웹만 누락).
              //   scope는 일부러 지정하지 않는다 — 콘솔 미설정 동의항목을 요청하면 로그인 자체가 깨진다.
              //   동의항목이 켜져 있으면 phone_number가 실려 오고, 아니면 지금처럼 빈값 폴백.
              phone: normalizeSocialPhone(account.phone_number),
              accessToken
            });
          },
          fail: err => {
            console.error('카카오 사용자 정보 요청 실패', err);
            reject(err);
          }
        });
      },
      fail: err => {
        console.error('카카오 로그인 실패', err);
        reject(err);
      }
    });
  });
};

/**
 * 네이버 웹 로그인
 * - 리다이렉트 방식 (implicit) → NaverCallback.vue에서 SDK로 프로필 조회
 * - 네이버 개발자센터에서 Callback URL 등록 필요: {도메인}/oauth/naver/callback
 */
export const webNaverLogin = (forceReauth = false) => {
  const callbackUrl = `${window.location.origin}/oauth/naver/callback`;
  const state = Math.random().toString(36).substring(2);
  // 260818: forceReauth = 네이버 세션을 무시하고 로그인 폼을 강제로 띄운다(auth_type=reauthenticate).
  //   '이미 다른 네이버 계정으로 가입된 번호' 막다른 지점의 유일한 출구다 — 그냥 다시 로그인시키면
  //   붙어 있는 세션이 같은 계정을 자동 승인해 되돌아온다(무한루프 체감).
  //   앱에서도 쓴다: WebView가 http(s)에 shouldOverrideUrlLoading=false 라 제자리 로드되고,
  //   콜백도 같은 오리진(/oauth/naver/callback)이라 앱을 벗어나지 않는다. 네이티브 SDK로는
  //   계정 강제 선택이 불가능하다(logout()은 우리 토큰만 지우고 계정은 네이버 앱이 정한다).
  window.location.href =
    `https://nid.naver.com/oauth2.0/authorize?response_type=code` +
    `&client_id=${NAVER_CLIENT_ID}` +
    `&redirect_uri=${encodeURIComponent(callbackUrl)}` +
    `&state=${state}` +
    (forceReauth ? '&auth_type=reauthenticate' : '');
};

/**
 * 구글 웹 로그인
 * - Google Identity Services (GSI) 사용
 * - Google Cloud Console에서 웹 클라이언트 ID의 승인된 자바스크립트 원본에 도메인 등록 필요
 *
 * 260720 P0-B 검토 결과 (idToken 미포함 — 의도된 보류):
 * BE 서버검증(GoogleTokenVerifier 추정)은 OIDC id_token(JWKS 서명검증)을 요구하지만,
 * GIS의 OAuth2 implicit 토큰 클라이언트(initTokenClient)는 스펙상 access_token만 반환하고
 * id_token은 포함하지 않는다 — 이는 별도 플로우인 google.accounts.id(One Tap/Sign In With
 * Google 공식 버튼)로만 얻을 수 있다. 그 플로우는 현재의 "커스텀 버튼 클릭 → OAuth 동의 팝업"
 * UX와 다른 패러다임(One Tap 배너 또는 구글 공식 버튼 렌더)이라, 무리하게 병행 호출하면
 * 팝업 중복·레이스 컨디션·로그인 자체가 깨질 위험이 있다. 이번 태스크는 "기존 로그인 동작
 * 보존"이 최우선이므로 idToken/nonce는 설정하지 않고 넘어간다(BE는 LOG 모드라 provider
 * 토큰 없이도 통과 — ENFORCE 전환 전 별도 태스크로 GSI id-flow 전환 설계 필요).
 * @returns {Promise<{id: string, name: string, email: string, phone: string, accessToken: string}>}
 */
export const webGoogleLogin = () => {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google SDK가 로드되지 않았습니다.'));
      return;
    }

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile',
      callback: async tokenResponse => {
        if (tokenResponse.error) {
          reject(new Error(tokenResponse.error));
          return;
        }

        try {
          // access_token으로 사용자 정보 조회
          const res = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          });
          const userInfo = await res.json();

          resolve({
            id: userInfo.id,
            name: userInfo.name || '',
            email: userInfo.email || '',
            phone: '',
            // 260914 대표 컨펌(구글은 전화번호 없이 이름·아이디만으로 가입): BE가 이 access_token으로
            //   구글 tokeninfo에 되물어 sub(=id)를 확정해야 전화 없는 가입을 허용한다. 카카오·네이버가
            //   번호 스킵 근거로 accessToken을 싣는 것과 같은 자리. id_token은 GIS 토큰 클라이언트가
            //   안 주므로(위 260720 주석) BE 구글 검증기가 access_token 경로를 따로 갖는다.
            accessToken: tokenResponse.access_token || ''
          });
        } catch (err) {
          console.error('구글 사용자 정보 요청 실패', err);
          reject(err);
        }
      },
      error_callback: err => {
        console.error('구글 로그인 실패', err);
        reject(new Error('구글 로그인이 취소되었습니다.'));
      }
    });

    tokenClient.requestAccessToken();
  });
};

/**
 * 웹 소셜 로그인 통합 함수
 * @param {string} type - JOIN_TYPE 값 (KAKAO, NAVER, GOOGLE)
 * @returns {Promise<{id: string, name: string, email: string, phone: string, accessToken?: string, idToken?: string, nonce?: string}>}
 */
export const webSocialLogin = async type => {
  switch (type) {
    case JOIN_TYPE.KAKAO.value:
      return webKakaoLogin();
    case JOIN_TYPE.NAVER.value:
      return webNaverLogin();
    case JOIN_TYPE.GOOGLE.value:
      return webGoogleLogin();
    default:
      throw new Error(`지원하지 않는 소셜 로그인 타입: ${type}`);
  }
};
